/**
 * Session Recorder for StorySplat Analytics
 *
 * Records viewer interactions (camera movement, waypoint changes, hotspot clicks,
 * portal activations, mode changes) and sends them to the StorySplat API for analytics.
 */

import type { ViewerInstance } from '../types';
import { getOrCreateVisitorId, isAnalyticsAllowed, safeRandomUUID } from './analyticsConsent';

type AnalyticsEventType = 'waypoint' | 'mode' | 'hotspot' | 'portal' | 'ui' | 'menu' | 'link';
type AnalyticsEvent = { type: AnalyticsEventType; name: string };
type ViewerEventName = Parameters<ViewerInstance['on']>[0];
type ViewerEventCallback = Parameters<ViewerInstance['on']>[1];

/** New scene attribution for a portal teleport. See `SessionRecorder.retarget`. */
export type SessionRecorderTarget = { sceneId?: string; ownerId?: string };

export type SessionRecorderHandle = {
  destroy: () => void;
  beginPortalTransition: () => void;
  attachViewer: (viewer: ViewerInstance, target?: SessionRecorderTarget) => void;
};

// ── Timeline entry types for session recording ──────────────────────────

type TimelineEntry =
  | { t: number; k: 'c'; p: [number, number, number]; r: [number, number, number] }
  | { t: number; k: 'w'; n: string; i: number }
  | { t: number; k: 'h'; n: string; id: string; p: [number, number, number]; ct?: string }
  | { t: number; k: 'P'; n: string; tid: string }
  | { t: number; k: 'u'; c: AnalyticsEventType; n: string }
  | { t: number; k: 'm'; m: string }
  | { t: number; k: 's'; v: number }
  | { t: number; k: 'x'; sx: number; sy: number }
  | { t: number; k: 'pb'; a: 'start' | 'stop' | 'complete' }
  | { t: number; k: 'xr'; a: 'start' | 'end'; xt: 'ar' | 'vr' | 'unknown'; d?: number }
  | { t: number; k: 'ld'; ms: number; b: number }
  | { t: number; k: 'er'; m: string };

// ── SessionRecorder ─────────────────────────────────────────────────────

class SessionRecorder {
  private static readonly QUEUE_KEY = 'storysplat_analytics_queue';
  private static readonly MAX_QUEUE_SIZE = 10;
  // Drop queued entries older than this on drain — keeps a background tab that
  // was abandoned hours ago from stitching stale timelines into a fresh session
  // when the user comes back.
  private static readonly QUEUE_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour
  // Per-request timeout so one slow retry can't stall the whole flush.
  private static readonly RETRY_FETCH_TIMEOUT_MS = 5_000;

  // `progressUpdate` fires once per RENDERED FRAME during tour playback, so an
  // unthrottled entry-per-emission produced ~1800 entries per 30s chunk at
  // 60fps and ~3600 at 120Hz — past the server's 2000-entry ceiling, which
  // answers 400. 400 is (correctly) not retryable and the buffer has already
  // been drained by then, so the ENTIRE chunk, camera samples included, was
  // destroyed; on chunk 0 that also cost the session its `startedAt` and it
  // vanished from the Sessions tab. Progress deltas are what matter for replay,
  // not frames: cap at ~2 Hz and require a 1% move, with the 0/1 endpoints
  // always allowed through so tour start/completion is never missed.
  private static readonly PROGRESS_MIN_INTERVAL_MS = 500;
  private static readonly PROGRESS_MIN_DELTA = 0.01;

  // Client-side ceiling kept well below the server's MAX_TIMELINE_EVENTS (2000)
  // so a burst can never cost us a whole chunk. On overflow we shed the
  // lowest-value entries instead (see `trimTimeline`).
  private static readonly MAX_TIMELINE_ENTRIES = 1_500;

  // Engagement floor for counting a session that produced zero discrete
  // interactions (see `hasReportableEngagement`). 10s of wall clock plus more
  // than the single forced camera sample the constructor takes is above what a
  // crawler or an instant bounce produces, and below the shortest intentional
  // look at a scene.
  private static readonly PASSIVE_SESSION_MIN_MS = 10_000;
  private static readonly PASSIVE_SESSION_MIN_TIMELINE = 2;

  // Only responses in this set should be retried. Permanent 4xx (400 schema,
  // 403, 404, 413 too-large) will never succeed on replay — retrying them
  // just burns the rate-limit budget for legitimate transient retries.
  private static isRetryableStatus(status: number): boolean {
    return status === 408 || status === 429 || status >= 500;
  }

  private timeline: TimelineEntry[] = [];
  private pendingEvents: AnalyticsEvent[] = [];
  // Absolute start of the visitor session. Never rebased — it backs the
  // 30-minute recording cap, which is a per-visitor budget.
  private sessionStart = Date.now();
  // Start of the CURRENT scene's attribution window. Equals `sessionStart`
  // until a portal teleport retargets the recorder (see `retarget`).
  private sceneStart = Date.now();
  private sessionId: string;
  private visitorId: string;
  private chunkIndex = 0;
  // Chunk counters, one per scene visited in this session. Chunks are stored at
  // `sceneAnalytics/{sceneId}/sessions/{sessionId}/chunks/{chunkIndex}` and
  // written with `set()`, so the index has to be unique per (scene, session) —
  // a plain reset to 0 on every retarget made a return trip (A → B → A, which
  // is what any bidirectional portal produces) rewrite the first visit's chunk 0
  // on top of itself, and silently: the server skips its chunkCount increment
  // when the doc already exists, so the session kept advertising a chunk count
  // whose data was gone.
  private chunkIndexBySceneId = new Map<string, number>();
  // Time already spent in each scene earlier in this cross-scene journey.
  // A bidirectional portal can produce A -> B -> A; the backend expects every
  // later duration for (sceneId, sessionId) to be cumulative so max/delta
  // accounting preserves both A legs and ignores stale duplicate beacons.
  private elapsedMsBySceneId = new Map<string, number>();
  // Monotonic count of timeline entries recorded for the current scene. Unlike
  // `timeline.length` this survives chunk flushes, so passive-engagement
  // detection still works after the buffer has been drained.
  private timelineEntryCount = 0;
  private lastProgressAt = 0;
  private lastProgressValue: number | null = null;
  private xrStartedAt: number | null = null;
  private xrType: 'ar' | 'vr' | 'unknown' = 'unknown';
  private lastPos = { x: 0, y: 0, z: 0 };
  private lastRot = { x: 0, y: 0, z: 0 };
  private cameraIntervalId: ReturnType<typeof setInterval> | null = null;
  private flushIntervalId: ReturnType<typeof setInterval> | null = null;
  private maxDurationMs = 30 * 60 * 1000; // 30 minutes
  private destroyed = false;
  private beaconFlushed = false;
  // Earliest time (ms since epoch) the retry queue may be drained again.
  // Bumped by Retry-After on 429 responses so we don't immediately re-hit a
  // rate-limited endpoint and waste more of its budget.
  private retryNotBeforeMs = 0;

  private handleVisibility: () => void;
  private handleUnload: () => void;
  private handlePageHide: () => void;
  private handleCanvasClick: ((e: MouseEvent) => void) | null = null;
  private handleUiClick: ((e: MouseEvent) => void) | null = null;
  private viewerContainer: HTMLElement | null = null;
  private samplingPaused = false;
  private viewerEventBindings: Array<{
    viewer: ViewerInstance;
    event: ViewerEventName;
    callback: ViewerEventCallback;
  }> = [];

  constructor(
    private viewer: ViewerInstance,
    private baseUrl: string,
    private sceneId: string,
    private ownerId: string
  ) {
    // Generate IDs. `safeRandomUUID` never throws — `crypto.randomUUID` is
    // secure-context-only, and letting it throw here took the whole viewer
    // down on plain-http embeds. The visitor id is persisted to localStorage
    // (shared with the view ping and the injected tracker in the HTML
    // endpoint); an opted-out visitor gets an ephemeral id and no write.
    this.sessionId = safeRandomUUID();
    this.visitorId = getOrCreateVisitorId() ?? safeRandomUUID();

    // Bind handlers
    this.handleVisibility = () => {
      if (document.visibilityState === 'hidden') this.beaconFlush(false);
    };
    this.handleUnload = () => this.beaconFlush(true);
    // `beforeunload` never fires reliably on iOS Safari or on a bfcache
    // freeze, so the session-end beacon (duration + completion) was simply
    // lost for most mobile visitors. `pagehide` is the one event that fires
    // in both cases — same pattern crossSceneAudio.ts uses. `beaconFlush`
    // latches on `beaconFlushed`, so pagehide + beforeunload both firing
    // still sends exactly one final payload.
    this.handlePageHide = () => this.beaconFlush(true);

    this.setupEventListeners();
    this.sampleCamera(true);
    this.startCameraSampling();
    this.startFlushInterval();

    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('beforeunload', this.handleUnload);
    window.addEventListener('pagehide', this.handlePageHide);
  }

  private ts(): number {
    return Date.now() - this.sceneStart;
  }

  private cumulativeSceneDurationMs(): number {
    return (this.elapsedMsBySceneId.get(this.sceneId) ?? 0) + this.ts();
  }

  private quantize(v: number, precision: number): number {
    return Math.round(v / precision) * precision;
  }

  private safeEventName(name: string): string {
    return String(name || '')
      .replace(/[^a-zA-Z0-9 _-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 64)
      .trim() || 'unknown';
  }

  private isSafeTrackingId(id: string): boolean {
    return /^[a-zA-Z0-9_-]{1,128}$/.test(id);
  }

  private safeHostname(value: string): string {
    const host = String(value || '').trim().toLowerCase().replace(/:\d+$/, '').slice(0, 200);
    if (!host || host.includes('..') || !/^[a-z0-9.-]{1,200}$/.test(host)) return '';
    return host;
  }

  private safeUrlForAnalytics(value: string, maxLength: number): string {
    const raw = String(value || '').trim();
    if (!raw) return '';
    try {
      const url = new URL(raw);
      if (!/^https?:$/.test(url.protocol)) return '';
      return `${url.origin}${url.pathname}`.slice(0, maxLength);
    } catch {
      return raw.replace(/[\u0000-\u001F<>]/g, '').slice(0, maxLength);
    }
  }

  private getEmbedSourceMeta(): { hostname: string; referrer: string; embedUrl: string } {
    try {
      return {
        hostname: this.safeHostname(window.location.hostname || ''),
        referrer: this.safeUrlForAnalytics(document.referrer || '', 500),
        embedUrl: this.safeUrlForAnalytics(window.location.href || '', 500),
      };
    } catch {
      return { hostname: '', referrer: '', embedUrl: '' };
    }
  }

  // Single entry point for timeline writes so the client-side cap and the
  // engagement counter can never be bypassed by a new call site.
  private pushTimeline(entry: TimelineEntry): void {
    if (this.timeline.length >= SessionRecorder.MAX_TIMELINE_ENTRIES) {
      this.trimTimeline();
    }
    this.timeline.push(entry);
    this.timelineEntryCount++;
  }

  // Shed the lowest-value entries first — progress samples ('s'), then camera
  // samples ('c'), then whatever is oldest. Interactions (waypoint, hotspot,
  // portal, ui, mode, playback, xr) are what the Sessions tab is actually
  // built on, so they are the last thing to go. Losing a few samples beats
  // losing the whole chunk to a server 400.
  private trimTimeline(): void {
    const target = Math.floor(SessionRecorder.MAX_TIMELINE_ENTRIES * 0.9);
    let excess = this.timeline.length - target;
    if (excess <= 0) return;

    for (const kind of ['s', 'c'] as const) {
      if (excess <= 0) break;
      const kept: TimelineEntry[] = [];
      for (const entry of this.timeline) {
        if (excess > 0 && entry.k === kind) {
          excess--;
          continue;
        }
        kept.push(entry);
      }
      this.timeline = kept;
    }

    if (excess > 0) this.timeline.splice(0, excess);
  }

  private recordInteraction(type: AnalyticsEventType, name: string): void {
    const safeName = this.safeEventName(name);
    this.pendingEvents.push({ type, name: safeName });
    this.pushTimeline({ t: this.ts(), k: 'u', c: type, n: safeName });
  }

  private elementText(el: HTMLElement): string {
    return (
      el.getAttribute('aria-label') ||
      el.getAttribute('title') ||
      el.textContent ||
      el.dataset.linkId ||
      el.dataset.portalId ||
      el.getAttribute('href') ||
      ''
    ).trim();
  }

  private describeButton(el: HTMLElement): string {
    const classList = el.classList;
    if (classList.contains('storysplat-btn-prev')) return 'previous waypoint';
    if (classList.contains('storysplat-btn-next')) return 'next waypoint';
    if (classList.contains('storysplat-btn-play')) return 'play pause';
    if (classList.contains('storysplat-mode-btn')) return `camera mode ${el.dataset.mode || this.elementText(el)}`;
    if (classList.contains('storysplat-explore-btn')) return `explore mode ${el.dataset.exploreMode || this.elementText(el)}`;
    if (classList.contains('storysplat-fullscreen-btn')) return 'fullscreen';
    if (classList.contains('storysplat-mute-btn')) return 'mute toggle';
    if (classList.contains('storysplat-help-btn')) return 'help';
    if (classList.contains('storysplat-help-tab')) return `help tab ${el.dataset.tab || this.elementText(el)}`;
    if (classList.contains('storysplat-share-btn')) return 'share';
    if (classList.contains('storysplat-photo-btn')) return 'photo mode';
    if (classList.contains('storysplat-photo-take')) return 'photo take';
    if (classList.contains('storysplat-photo-exit')) return 'photo mode exit';
    if (classList.contains('storysplat-photo-share-close')) return 'photo share close';
    if (classList.contains('storysplat-photo-share-action')) return `photo share ${this.elementText(el)}`;
    if (classList.contains('storysplat-photo-share-download')) return 'photo share download';
    if (classList.contains('storysplat-photo-share-copy')) return 'photo share copy link';
    if (classList.contains('storysplat-relight-btn')) return 'relighting toggle';
    if (classList.contains('storysplat-fisheye-btn')) return 'tiny planet toggle';
    if (classList.contains('storysplat-stereo-btn')) return '3D view menu';
    if (classList.contains('storysplat-stereo-option')) return `3D view ${this.elementText(el)}`;
    if (classList.contains('storysplat-waypoint-list-toggle')) return 'waypoint menu toggle';
    if (classList.contains('storysplat-scene-menu-toggle')) return 'scene menu toggle';
    if (classList.contains('storysplat-portal-popup-confirm')) return 'portal confirm';
    if (classList.contains('storysplat-portal-popup-cancel')) return 'portal cancel';
    // Must come before the close check: the media popup's fullscreen toggle
    // used to reuse the close button's class purely for styling, so every
    // fullscreen click was recorded as "hotspot popup close".
    if (classList.contains('storysplat-hotspot-popup-fullscreen')) return 'hotspot popup fullscreen';
    if (classList.contains('storysplat-hotspot-popup-close')) return 'hotspot popup close';
    if (classList.contains('storysplat-measure-btn')) return 'measure toggle';
    if (classList.contains('storysplat-skins-btn')) return 'virtual staging toggle';
    if (classList.contains('storysplat-skin-exit-btn')) return 'virtual staging exit';
    if (classList.contains('storysplat-vr-btn')) return 'vr';
    if (classList.contains('storysplat-ar-btn')) return 'ar';
    if (classList.contains('storysplat-lazy-load-start-btn')) return 'start experience';

    const text = this.elementText(el);
    if (text) return text;

    const storyClass = Array.from(classList).find(c => c.startsWith('storysplat-'));
    return storyClass ? storyClass.replace(/^storysplat-/, '').replace(/-/g, ' ') : 'button';
  }

  private trackStagingView(skinId: string): void {
    if (!this.isSafeTrackingId(skinId)) return;

    const url = `${this.baseUrl}/api/track-embed`;
    const headers = { 'Content-Type': 'application/json' };
    const body = JSON.stringify({
      sceneId: this.sceneId,
      ownerId: this.ownerId,
      type: 'staging-view',
      skinId,
      visitorId: this.visitorId,
      sessionId: this.sessionId,
    });

    fetch(url, { method: 'POST', headers, body })
      .then((res) => {
        if (!res.ok && SessionRecorder.isRetryableStatus(res.status)) {
          this.saveToRetryQueue(body);
          if (res.status === 429) this.noteRetryAfter(res);
        }
      })
      .catch(() => {
        this.saveToRetryQueue(body);
      });
  }

  private trackUiClick(event: MouseEvent): void {
    if (this.destroyed) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target === this.viewer.canvas || target.closest('canvas') === this.viewer.canvas) return;

    const interactive = target.closest(
      '.storysplat-scene-menu-item, .storysplat-scene-menu-folder-header, .storysplat-waypoint-item, .storysplat-hotspot-popup-link, .storysplat-skin-item, button, a, [role="button"]'
    ) as HTMLElement | null;
    if (!interactive || !this.viewerContainer?.contains(interactive)) return;

    if (interactive instanceof HTMLButtonElement && interactive.disabled) return;

    if (interactive.classList.contains('storysplat-skin-item')) {
      const skinId = interactive.dataset.skinId || interactive.getAttribute('data-skin-id') || '';
      const index = Number(interactive.dataset.skinIndex);
      const fallback = Number.isFinite(index) ? `staging ${index + 1}` : 'virtual staging image';
      const label = this.elementText(interactive) || fallback;
      this.recordInteraction('ui', `virtual staging ${label}`);
      if (skinId) this.trackStagingView(skinId);
      return;
    }

    if (interactive.classList.contains('storysplat-scene-menu-item')) {
      const label = this.elementText(interactive);
      if (interactive.dataset.portalId) {
        this.recordInteraction('menu', `portal ${label || interactive.dataset.portalId}`);
        return;
      }
      if (interactive.dataset.linkId) {
        const contentType = interactive.dataset.contentType || 'link';
        this.recordInteraction('menu', `${contentType} ${label || interactive.dataset.linkId}`);
        if (contentType === 'link') {
          this.recordInteraction('link', `menu ${label || interactive.dataset.linkId}`);
        }
        return;
      }
      this.recordInteraction('menu', label || 'scene menu item');
      return;
    }

    if (interactive.classList.contains('storysplat-scene-menu-folder-header')) {
      this.recordInteraction('menu', `folder ${this.elementText(interactive)}`);
      return;
    }

    if (interactive.classList.contains('storysplat-waypoint-item')) {
      const index = interactive.dataset.waypointIndex;
      this.recordInteraction('menu', `waypoint ${index ? Number(index) + 1 : this.elementText(interactive)}`);
      return;
    }

    if (interactive.classList.contains('storysplat-hotspot-popup-link')) {
      this.recordInteraction('link', `hotspot ${this.elementText(interactive)}`);
      return;
    }

    if (interactive instanceof HTMLAnchorElement) {
      const label = this.elementText(interactive);
      this.recordInteraction('link', label || interactive.href || 'link');
      return;
    }

    this.recordInteraction('ui', this.describeButton(interactive));
  }

  // ── Camera Sampling (1Hz with delta compression) ──

  private sampleCamera(force = false): void {
    // The 30-minute cap is a per-visitor budget, so it reads the absolute
    // session clock — a portal teleport rebases `ts()` but must not hand the
    // visitor another 30 minutes of sampling.
    if (this.destroyed || this.samplingPaused || Date.now() - this.sessionStart > this.maxDurationMs) return;

    try {
      const pos = this.viewer.getPosition();
      const rot = this.viewer.getRotation();

      // Delta compression: skip if barely moved
      const dPos = Math.abs(pos.x - this.lastPos.x) + Math.abs(pos.y - this.lastPos.y) + Math.abs(pos.z - this.lastPos.z);
      const dRot = Math.abs(rot.x - this.lastRot.x) + Math.abs(rot.y - this.lastRot.y) + Math.abs(rot.z - this.lastRot.z);

      if (!force && dPos < 0.1 && dRot < 2) return;

      this.lastPos = { ...pos };
      this.lastRot = { ...rot };

      this.pushTimeline({
        t: this.ts(),
        k: 'c',
        p: [this.quantize(pos.x, 0.01), this.quantize(pos.y, 0.01), this.quantize(pos.z, 0.01)],
        r: [this.quantize(rot.x, 0.1), this.quantize(rot.y, 0.1), this.quantize(rot.z, 0.1)],
      });
    } catch {
      // The viewer may be between scenes during portal navigation.
    }
  }

  private startCameraSampling(): void {
    this.cameraIntervalId = setInterval(() => this.sampleCamera(), 1000);
  }

  private observeViewerEvent(event: ViewerEventName, callback: ViewerEventCallback): void {
    const obs = this.viewer.onInternal ? this.viewer.onInternal.bind(this.viewer) : this.viewer.on.bind(this.viewer);
    obs(event, callback);
    this.viewerEventBindings.push({ viewer: this.viewer, event, callback });
  }

  // ── Event Listeners ──

  private setupEventListeners(): void {
    // waypointChange emits: { index, waypoint: WaypointData, prevIndex, cameraMode }
    this.observeViewerEvent('waypointChange', (data?: { waypoint?: { name?: string }; name?: string; index?: number }) => {
      const name = data?.waypoint?.name || data?.name || `waypoint-${data?.index ?? 0}`;
      this.pendingEvents.push({ type: 'waypoint', name });
      this.pushTimeline({ t: this.ts(), k: 'w', n: name, i: data?.index ?? 0 });
    });

    this.observeViewerEvent('modeChange', (data?: { mode?: string }) => {
      const mode = data?.mode || 'unknown';
      this.pendingEvents.push({ type: 'mode', name: mode });
      this.pushTimeline({ t: this.ts(), k: 'm', m: mode });
    });

    // hotspotClick emits: { hotspot: HotspotData } — HotspotData has .title and .id, no .name.
    // `contentType` rides along on HotspotData and is the natural segmentation
    // for "which kind of content actually gets opened", so keep it.
    this.observeViewerEvent('hotspotClick', (data?: { hotspot?: { title?: string; name?: string; id?: string; contentType?: string; position?: { x: number; y: number; z: number } } }) => {
      const name = data?.hotspot?.title || data?.hotspot?.name || 'unknown';
      this.pendingEvents.push({ type: 'hotspot', name });
      const p = data?.hotspot?.position;
      const contentType = this.safeEventName(data?.hotspot?.contentType || '');
      this.pushTimeline({
        t: this.ts(), k: 'h', n: name, id: data?.hotspot?.id || '',
        p: p ? [p.x, p.y, p.z] : [0, 0, 0],
        ...(data?.hotspot?.contentType ? { ct: contentType } : {}),
      });
    });

    // portalActivated emits: { portalId, targetSceneId, targetSceneName } (flat, not nested)
    this.observeViewerEvent('portalActivated', (data?: { targetSceneName?: string; targetSceneId?: string; portal?: { name?: string; targetSceneId?: string } }) => {
      const name = data?.targetSceneName || data?.portal?.name || 'unknown';
      this.pendingEvents.push({ type: 'portal', name });
      this.pushTimeline({ t: this.ts(), k: 'P', n: name, tid: data?.targetSceneId || data?.portal?.targetSceneId || '' });
    });

    // Throttled — see PROGRESS_MIN_INTERVAL_MS. One entry per frame overran the
    // server's per-chunk ceiling and cost the whole chunk.
    this.observeViewerEvent('progressUpdate', (data?: { progress?: number }) => {
      if (data?.progress === undefined || !Number.isFinite(data.progress)) return;
      const value = Math.round(data.progress * 1000) / 1000;
      const now = Date.now();
      const isFirst = this.lastProgressValue === null;
      // Endpoints are load-bearing for "did they finish the tour?" — never drop
      // them to the throttle, but only record each one once.
      const isEndpoint = (value === 0 || value === 1) && value !== this.lastProgressValue;
      const movedEnough = isFirst
        || Math.abs(value - (this.lastProgressValue ?? 0)) >= SessionRecorder.PROGRESS_MIN_DELTA;
      const intervalElapsed = now - this.lastProgressAt >= SessionRecorder.PROGRESS_MIN_INTERVAL_MS;
      if (!isFirst && !isEndpoint && !(movedEnough && intervalElapsed)) return;

      this.lastProgressAt = now;
      this.lastProgressValue = value;
      this.pushTimeline({ t: this.ts(), k: 's', v: value });
    });

    this.observeViewerEvent('playbackStart', () => {
      this.pushTimeline({ t: this.ts(), k: 'pb', a: 'start' });
    });
    this.observeViewerEvent('playbackStop', () => {
      this.pushTimeline({ t: this.ts(), k: 'pb', a: 'stop' });
    });
    this.observeViewerEvent('playbackComplete', () => {
      this.pushTimeline({ t: this.ts(), k: 'pb', a: 'complete' });
    });

    // AR/VR entry + dwell time. Previously emitted and observed by nobody, so
    // the headline AR differentiator had zero usage data. The aggregate event
    // is what separates a successful XR entry from an AR *button* click (which
    // the UI click tracker already records).
    this.observeViewerEvent('xrStart', (data?: { type?: string }) => {
      const xrType = data?.type === 'ar' || data?.type === 'vr' ? data.type : 'unknown';
      this.xrType = xrType;
      this.xrStartedAt = Date.now();
      this.pendingEvents.push({ type: 'ui', name: `${xrType} session` });
      this.pushTimeline({ t: this.ts(), k: 'xr', a: 'start', xt: xrType });
    });

    this.observeViewerEvent('xrEnd', () => {
      const durationMs = this.xrStartedAt !== null ? Date.now() - this.xrStartedAt : undefined;
      this.pushTimeline({
        t: this.ts(), k: 'xr', a: 'end', xt: this.xrType,
        ...(durationMs !== undefined ? { d: durationMs } : {}),
      });
      this.xrStartedAt = null;
      this.xrType = 'unknown';
    });

    // Load performance. `loaded` carries loadTimeMs + bandwidthUsed; recording
    // it makes pre-first-frame abandon computable (session with no 'ld' entry).
    this.observeViewerEvent('loaded', (data?: { loadTimeMs?: number; bandwidthUsed?: number }) => {
      this.pushTimeline({
        t: this.ts(), k: 'ld',
        ms: Math.max(0, Math.round(Number(data?.loadTimeMs) || 0)),
        b: Math.max(0, Math.round(Number(data?.bandwidthUsed) || 0)),
      });
    });

    // A viewer 'error' is terminal for the recorder, but tear-down used to be
    // the ONLY thing it did — failed sessions were unattributable. Record the
    // reason first, then destroy (which beacon-flushes it out).
    this.observeViewerEvent('error', (err?: { message?: string } | string) => {
      const message = typeof err === 'string' ? err : err?.message || 'unknown';
      this.pushTimeline({ t: this.ts(), k: 'er', m: this.safeEventName(message) });
      this.destroy();
    });

    // Screen click tracking
    try {
      this.handleCanvasClick = (e: MouseEvent) => {
        this.pushTimeline({ t: this.ts(), k: 'x', sx: e.clientX, sy: e.clientY });
      };
      this.viewer.canvas.addEventListener('click', this.handleCanvasClick);
    } catch {
      // canvas may not be accessible
    }

    // Viewer UI click tracking. Use capture so menu/link handlers that stop
    // propagation still get counted.
    try {
      this.viewerContainer = (this.viewer.canvas.closest('.storysplat-viewer-container') as HTMLElement | null)
        || this.viewer.canvas.parentElement;
      if (this.viewerContainer) {
        this.handleUiClick = (e: MouseEvent) => this.trackUiClick(e);
        this.viewerContainer.addEventListener('click', this.handleUiClick, true);
      }
    } catch {
      // container may not be accessible
    }
  }

  private detachDomListeners(): void {
    if (this.handleCanvasClick) {
      try { this.viewer.canvas.removeEventListener('click', this.handleCanvasClick); } catch { /* */ }
      this.handleCanvasClick = null;
    }
    if (this.handleUiClick && this.viewerContainer) {
      try { this.viewerContainer.removeEventListener('click', this.handleUiClick, true); } catch { /* */ }
      this.handleUiClick = null;
      this.viewerContainer = null;
    }
  }

  private detachViewerEventListeners(): void {
    for (const binding of this.viewerEventBindings) {
      try { binding.viewer.off(binding.event, binding.callback); } catch { /* */ }
    }
    this.viewerEventBindings = [];
  }

  beginPortalTransition(): void {
    if (this.destroyed) return;
    this.samplingPaused = true;
    this.detachDomListeners();
    this.detachViewerEventListeners();
  }

  /**
   * Re-point the recorder at the scene a portal teleported into.
   *
   * `sceneId`/`ownerId` were constructor parameter properties and `attachViewer`
   * only ever rebound `this.viewer`, so the portal path's freshly-computed
   * `analytics.sceneId` was discarded and every payload for the rest of the
   * visit was stamped with the ORIGIN scene.
   *
   * Decision — the session CONTINUES: the same `sessionId` spans the teleport,
   * which is what makes cross-scene journey analysis possible (the destination
   * session doc can be joined back to the origin on sessionId). What re-targets
   * is scene ATTRIBUTION:
   *  - the origin scene's buffered timeline + events are flushed first, as a
   *    `session-end`, so they can't be misfiled under the destination;
   *  - `chunkIndex` resumes THIS scene's counter, because chunks are keyed under
   *    `sceneAnalytics/{sceneId}/sessions/{sessionId}/chunks/{chunkIndex}` and
   *    the server only writes `startedAt`/meta on chunk 0. A first visit starts
   *    at 0, so the destination still gets its start time and shows up in the
   *    Sessions tab; a RETURN visit resumes past the chunks already written
   *    instead of overwriting them, and correctly sends no second `meta` — that
   *    scene's session doc was stamped on the first visit;
   *  - the timeline clock rebases so the destination's replay starts at t=0 and
   *    its reported duration is time-in-that-scene, not time-since-first-scene.
   */
  retarget(target: SessionRecorderTarget): void {
    if (this.destroyed) return;
    const nextSceneId = target.sceneId || this.sceneId;
    const nextOwnerId = target.ownerId || this.ownerId;
    if (nextSceneId === this.sceneId && nextOwnerId === this.ownerId) return;

    // Close out the OLD scene before any field changes.
    this.flushSceneAttribution();
    this.elapsedMsBySceneId.set(this.sceneId, this.cumulativeSceneDurationMs());

    this.sceneId = nextSceneId;
    this.ownerId = nextOwnerId;
    this.chunkIndex = this.chunkIndexBySceneId.get(nextSceneId) ?? 0;
    this.sceneStart = Date.now();
    this.timelineEntryCount = 0;
    this.lastProgressAt = 0;
    this.lastProgressValue = null;
    // Force the next camera sample so the destination gets an opening pose
    // instead of being delta-compressed against the origin scene's camera.
    this.lastPos = { x: 0, y: 0, z: 0 };
    this.lastRot = { x: 0, y: 0, z: 0 };
  }

  attachViewer(viewer: ViewerInstance, target?: SessionRecorderTarget): void {
    if (this.destroyed) return;
    this.detachDomListeners();
    this.detachViewerEventListeners();
    if (target) this.retarget(target);
    this.viewer = viewer;
    this.samplingPaused = false;
    this.setupEventListeners();
    this.sampleCamera(true);
  }

  // ── Flush Logic ──

  private startFlushInterval(): void {
    this.flushIntervalId = setInterval(() => this.flush(), 30_000);
  }

  private buildAnalyticsPayload() {
    const sessionDuration = Math.round(this.cumulativeSceneDurationMs() / 1000);
    const events = this.pendingEvents.splice(0, 100);
    const sourceMeta = this.getEmbedSourceMeta();
    return {
      sceneId: this.sceneId,
      ownerId: this.ownerId,
      type: 'analytics' as const,
      // One id per logical aggregate payload. The serialized body is retained
      // verbatim in the retry queue, so network retries reuse this id while
      // independent/overlapping flushes receive different ids and may arrive
      // out of order safely.
      analyticsReportId: safeRandomUUID(),
      // sessionId lets the server dedup on (visitorId, sessionId) so a retried
      // stale payload that crosses the time-window dedup boundary can't be
      // counted as a fresh session.
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      sessionDuration,
      events,
      hostname: sourceMeta.hostname,
      referrer: sourceMeta.referrer,
    };
  }

  /**
   * Drain every pending aggregate event into backend-sized batches.
   *
   * Retarget and destroy are attribution boundaries: leaving a tail after the
   * first 100 events would either file it under the next scene or discard it.
   * Passive engagement still produces one empty-event aggregate.
   */
  private buildAnalyticsPayloads() {
    if (!this.hasReportableEngagement()) return [];
    const payloads = [this.buildAnalyticsPayload()];
    while (this.pendingEvents.length > 0) payloads.push(this.buildAnalyticsPayload());
    return payloads;
  }

  private buildSessionChunkPayload(isFinal: boolean) {
    const entries = this.timeline.splice(0);
    const payload: Record<string, unknown> = {
      sceneId: this.sceneId,
      ownerId: this.ownerId,
      type: isFinal ? 'session-end' as const : 'session-chunk' as const,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      chunkIndex: this.chunkIndex,
      timeline: entries,
    };

    // First chunk includes meta
    if (this.chunkIndex === 0) {
      const sourceMeta = this.getEmbedSourceMeta();
      let initialMode = 'tour';
      // On a portal retarget this runs while the origin viewer is being torn
      // down, so getCameraMode() may already be gone. Meta must never be the
      // reason a chunk-0 payload (and with it the session's startedAt) is lost.
      try { initialMode = this.viewer.getCameraMode(); } catch { /* viewer between scenes */ }
      payload.meta = {
        // MacIntel + multi-touch = desktop-UA iPad (same signal as the viewer's
        // isDesktopUAiPad), which is served the mobile experience.
        device: /Mobi|Android/i.test(navigator.userAgent)
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ? 'mobile' : 'desktop',
        viewport: { w: window.innerWidth, h: window.innerHeight },
        userAgent: navigator.userAgent.slice(0, 200),
        initialMode,
        referrer: sourceMeta.referrer,
        embedUrl: sourceMeta.embedUrl,
        hostname: sourceMeta.hostname,
      };
    }

    if (isFinal) {
      const durationMs = this.cumulativeSceneDurationMs();
      payload.duration = Math.round(durationMs / 1000);
      payload.durationMs = durationMs;
    }

    this.chunkIndex++;
    this.chunkIndexBySceneId.set(this.sceneId, this.chunkIndex);
    return payload;
  }

  /**
   * Meaningful engagement worth reporting even with zero discrete interactions.
   *
   * `flush()` used to gate the aggregate on `pendingEvents.length > 0`, and
   * camera movement never populates `pendingEvents` — so a visitor who orbited
   * for four minutes and left incremented nothing, and average session duration
   * was silently biased toward interactive sessions. The floor (duration +
   * more than the constructor's single forced camera sample) is what keeps this
   * from turning bots and instant bounces into a flood of empty sessions.
   */
  private hasReportableEngagement(): boolean {
    if (this.pendingEvents.length > 0) return true;
    return this.timelineEntryCount >= SessionRecorder.PASSIVE_SESSION_MIN_TIMELINE
      && Date.now() - this.sceneStart >= SessionRecorder.PASSIVE_SESSION_MIN_MS;
  }

  // Drain any payloads that failed on a previous flush. Runs before every
  // new flush so flaky networks (hotel WiFi) don't silently lose events.
  //
  // Security: we deliberately ignore any `url` field stored in the queue and
  // always POST to the current `url`. A prior version accepted the stored URL,
  // which turned a same-origin XSS into a persistent beacon to attacker-chosen
  // endpoints.
  private async drainRetryQueue(url: string, headers: Record<string, string>): Promise<void> {
    // Respect a Retry-After received from a recent 429 — re-hitting the
    // endpoint early just returns another 429 and burns more budget.
    if (Date.now() < this.retryNotBeforeMs) return;

    let queue: { url?: string; body: string; savedAt: number }[];
    try {
      const raw = localStorage.getItem(SessionRecorder.QUEUE_KEY);
      if (!raw) return;
      queue = JSON.parse(raw);
      if (!Array.isArray(queue) || queue.length === 0) return;
    } catch {
      try { localStorage.removeItem(SessionRecorder.QUEUE_KEY); } catch { /* */ }
      return;
    }

    const now = Date.now();
    // Filter out stale entries up front so we don't waste a fetch on them.
    const fresh = queue.filter(e => e && typeof e.body === 'string' && typeof e.savedAt === 'number'
      && now - e.savedAt < SessionRecorder.QUEUE_MAX_AGE_MS);

    if (fresh.length === 0) {
      try { localStorage.removeItem(SessionRecorder.QUEUE_KEY); } catch { /* */ }
      return;
    }

    // Parallelize with an AbortController timeout per request so a slow network
    // can't pin the whole flush to 10 × fetch timeout seconds.
    const results = await Promise.allSettled(fresh.map(entry => {
      const ac = new AbortController();
      const timer = setTimeout(() => ac.abort(), SessionRecorder.RETRY_FETCH_TIMEOUT_MS);
      return fetch(url, { method: 'POST', headers, body: entry.body, signal: ac.signal })
        .finally(() => clearTimeout(timer));
    }));

    const remaining: typeof fresh = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') {
        const res = r.value;
        if (res.ok) return; // Success — drop from queue.
        // Non-2xx: only keep in queue if the status is actually retryable.
        // 4xx permanent failures (400/403/404/413) would otherwise re-POST
        // ~120 times per hour per entry and drain the session-chunk rate-limit
        // budget for the whole IP.
        if (SessionRecorder.isRetryableStatus(res.status)) {
          remaining.push(fresh[i]);
          if (res.status === 429) this.noteRetryAfter(res);
        }
      } else {
        // Network error / timeout — transient by nature, worth retrying.
        remaining.push(fresh[i]);
      }
    });

    try {
      if (remaining.length === 0) {
        localStorage.removeItem(SessionRecorder.QUEUE_KEY);
      } else {
        localStorage.setItem(SessionRecorder.QUEUE_KEY, JSON.stringify(remaining));
      }
    } catch { /* storage may be blocked */ }
  }

  // Parse a `Retry-After` header (HTTP-date or delta-seconds) and push the
  // next-allowed-drain timestamp forward. Clamps to [1s, 5m] so a hostile or
  // misconfigured backend can't suppress telemetry indefinitely.
  private noteRetryAfter(res: Response): void {
    const header = res.headers.get('Retry-After');
    if (!header) return;
    let delayMs = 0;
    const asNum = Number(header);
    if (Number.isFinite(asNum) && asNum >= 0) {
      delayMs = asNum * 1000;
    } else {
      const dateMs = Date.parse(header);
      if (!Number.isNaN(dateMs)) delayMs = Math.max(0, dateMs - Date.now());
    }
    delayMs = Math.min(Math.max(delayMs, 1_000), 5 * 60_000);
    this.retryNotBeforeMs = Math.max(this.retryNotBeforeMs, Date.now() + delayMs);
  }

  // Queue a failed payload for retry on next flush. Bounded to MAX_QUEUE_SIZE
  // entries (drops oldest first) so a long offline period can't blow up
  // localStorage quota. Only the body is stored; drain always resolves the
  // URL from the live recorder so a localStorage XSS can't redirect our POSTs.
  private saveToRetryQueue(body: string): void {
    try {
      const raw = localStorage.getItem(SessionRecorder.QUEUE_KEY);
      const queue: { body: string; savedAt: number }[] = raw ? JSON.parse(raw) : [];
      const next = Array.isArray(queue) ? queue : [];
      next.push({ body, savedAt: Date.now() });
      while (next.length > SessionRecorder.MAX_QUEUE_SIZE) next.shift();
      localStorage.setItem(SessionRecorder.QUEUE_KEY, JSON.stringify(next));
    } catch { /* storage may be blocked or quota exceeded */ }
  }

  private async flush(): Promise<void> {
    if (this.destroyed) return;

    const url = `${this.baseUrl}/api/track-embed`;
    const headers = { 'Content-Type': 'application/json' };

    await this.drainRetryQueue(url, headers);

    // Send analytics aggregate (existing system)
    for (const payload of this.buildAnalyticsPayloads()) {
      const body = JSON.stringify(payload);
      try {
        const res = await fetch(url, { method: 'POST', headers, body });
        if (!res.ok && SessionRecorder.isRetryableStatus(res.status)) {
          this.saveToRetryQueue(body);
          if (res.status === 429) this.noteRetryAfter(res);
        }
      } catch {
        // Network error - transient by nature.
        this.saveToRetryQueue(body);
      }
    }

    // Send session chunk
    if (this.timeline.length > 0) {
      const body = JSON.stringify(this.buildSessionChunkPayload(false));
      try {
        const res = await fetch(url, { method: 'POST', headers, body });
        if (!res.ok && SessionRecorder.isRetryableStatus(res.status)) {
          this.saveToRetryQueue(body);
          if (res.status === 429) this.noteRetryAfter(res);
        }
      } catch {
        this.saveToRetryQueue(body);
      }
    }
  }

  private sendBeaconPayload(payload: unknown): void {
    try {
      navigator.sendBeacon(
        `${this.baseUrl}/api/track-embed`,
        new Blob([JSON.stringify(payload)], { type: 'text/plain' })
      );
    } catch { /* silent — telemetry must never throw into the viewer */ }
  }

  /**
   * Close out the current scene's attribution window (portal teleport only).
   * Deliberately does NOT latch `beaconFlushed`: the page is still alive and
   * the destination scene must keep recording.
   */
  private flushSceneAttribution(): void {
    if (this.destroyed) return;
    for (const payload of this.buildAnalyticsPayloads()) this.sendBeaconPayload(payload);
    this.sendBeaconPayload(this.buildSessionChunkPayload(true));
  }

  // `beaconFlushed` latches on the first FINAL flush, which is what makes
  // pagehide + beforeunload (and destroy()) firing in any combination send
  // exactly one session-end.
  private beaconFlush(isFinal: boolean): void {
    if (this.destroyed || this.beaconFlushed) return;
    if (isFinal) this.beaconFlushed = true;

    // Analytics aggregate — send on real engagement, interactive or passive.
    for (const payload of this.buildAnalyticsPayloads()) this.sendBeaconPayload(payload);

    // Session chunk — always send on final to deliver the session-end signal,
    // even if timeline is empty (a preceding visibility flush may have drained it).
    if (this.timeline.length > 0 || isFinal) {
      this.sendBeaconPayload(this.buildSessionChunkPayload(isFinal));
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.beaconFlush(true);
    this.destroyed = true;
    if (this.cameraIntervalId) clearInterval(this.cameraIntervalId);
    if (this.flushIntervalId) clearInterval(this.flushIntervalId);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('beforeunload', this.handleUnload);
    window.removeEventListener('pagehide', this.handlePageHide);
    this.detachDomListeners();
    this.detachViewerEventListeners();
  }
}

/** Handle that records nothing — returned when the visitor has opted out. */
const NOOP_HANDLE: SessionRecorderHandle = {
  destroy: () => undefined,
  beginPortalTransition: () => undefined,
  attachViewer: () => undefined,
};

/**
 * Set up analytics event collection on the viewer.
 * Records both aggregate analytics and rich session timeline data.
 *
 * No-ops (and writes no visitor id) when the visitor has signalled opt-out via
 * DNT, Global Privacy Control, `?analytics=off`, or the
 * `storysplat_analytics_opt_out` localStorage key. `createViewer` performs the
 * same check before calling this; the check is repeated here because this is a
 * public export of the npm package.
 */
export function setupAnalyticsTracking(
  viewer: ViewerInstance,
  baseUrl: string,
  sceneId: string,
  ownerId: string
): SessionRecorderHandle {
  if (!isAnalyticsAllowed()) return NOOP_HANDLE;

  const recorder = new SessionRecorder(viewer, baseUrl, sceneId, ownerId);
  return {
    destroy: () => recorder.destroy(),
    beginPortalTransition: () => recorder.beginPortalTransition(),
    attachViewer: (nextViewer: ViewerInstance, target?: SessionRecorderTarget) =>
      recorder.attachViewer(nextViewer, target),
  };
}
